import * as THREE from 'three';
import { PLANET_R } from '../config.js';
import { groundRadius } from './placement.js';
import { spawnPickup, removePickup } from './pickups.js';

// Throwing system — spawns a small projectile that arcs along the sphere
// tangent plane under a local gravity. When it lands, it becomes a
// regular pickup on the ground (so the player can grab it again) *and*
// a "lure" that nearby creatures walk toward.
//
// Once a creature starts eating the lure (consumeLure), the pickup is
// locked (player can't take it back) and is removed after the eating
// duration, matching the creature's `eating` state.

const THROW_FORWARD_SPEED = 10;
const THROW_INITIAL_UP    = 5;
const THROW_GRAVITY       = 18;
const THROW_START_ALT     = 1.05;

const LURE_DURATION       = 22;     // seconds before an uneaten lure evaporates
const EATEN_VISIBLE_TIME  = 4.5;    // must match creatures.js EATING_TIME
const LURE_EFFECT_RANGE   = 7.5;

const projectiles = [];
const lures       = [];
let sceneRef  = null;
let planetRef = null;

const _axis = new THREE.Vector3();
const _quat = new THREE.Quaternion();

export function initThrowing(scene, planet) {
  sceneRef  = scene;
  planetRef = planet;
}

export function throwItem({ id, name, color, charPos, charForward }) {
  if (!sceneRef) return;
  const mesh = new THREE.Mesh(
    new THREE.OctahedronGeometry(0.1, 0),
    new THREE.MeshStandardMaterial({
      color, emissive: color, emissiveIntensity: 0.9,
      metalness: 0.3, roughness: 0.25,
    }),
  );
  mesh.castShadow = true;
  sceneRef.add(mesh);
  projectiles.push({
    mesh,
    charPos:     charPos.clone(),
    charForward: charForward.clone(),
    altitude:    THROW_START_ALT,
    vertV:       THROW_INITIAL_UP,
    id, name, color,
    active:      true,
  });
}

export function updateThrowing(dt) {
  const t = performance.now() / 1000;

  // ---- Projectiles ----
  for (const p of projectiles) {
    if (!p.active) continue;

    _axis.crossVectors(p.charPos, p.charForward);
    if (_axis.lengthSq() > 1e-8) {
      _axis.normalize();
      const ang = (THROW_FORWARD_SPEED * dt) / PLANET_R;
      _quat.setFromAxisAngle(_axis, ang);
      p.charPos.applyQuaternion(_quat).normalize();
      p.charForward.applyQuaternion(_quat);
    }

    p.altitude += p.vertV * dt;
    p.vertV    -= THROW_GRAVITY * dt;

    const gR = groundRadius(planetRef, p.charPos);
    p.mesh.position.copy(p.charPos).multiplyScalar(gR + Math.max(p.altitude, 0));
    p.mesh.rotation.x += 5 * dt;
    p.mesh.rotation.y += 4 * dt;

    if (p.altitude <= 0) {
      p.active = false;
      sceneRef.remove(p.mesh);
      _spawnLure(p.charPos, p.id, p.name, p.color);
    }
  }
  for (let i = projectiles.length - 1; i >= 0; i--) {
    if (!projectiles[i].active) projectiles.splice(i, 1);
  }

  // ---- Lures ----
  // The pickup's own animation (bob + spin) is handled by updatePickups;
  // here we only manage lifecycle — expire uneaten lures, and clean up
  // after a creature has finished eating one.
  for (const lure of lures) {
    if (lure.dead) continue;
    if (lure.consumed) {
      if (t - lure.consumedAt > EATEN_VISIBLE_TIME) {
        removePickup(lure.pickupEntry);
        lure.dead = true;
      }
    } else if (t - lure.born > LURE_DURATION) {
      removePickup(lure.pickupEntry);
      lure.dead = true;
    }
  }
  for (let i = lures.length - 1; i >= 0; i--) {
    if (lures[i].dead) lures.splice(i, 1);
  }
}

function _spawnLure(charPos, id, name, color) {
  const entry = spawnPickup(sceneRef, planetRef, {
    id: id || 'thrown_crystal',
    name: name || 'Crystal',
    color: color ?? 0xffcf50,
    shape: 'gem',
    icon: '◆',
    dir: charPos.clone().normalize(),
  });
  lures.push({
    pickupEntry: entry,
    worldPos:    entry.worldPos.clone(),
    consumed:    false,
    consumedAt:  -1,
    dead:        false,
    born:        performance.now() / 1000,
  });
}

// Find the nearest active lure within `maxDist` — used by the creature AI.
// Skips lures that have already been consumed (or picked up by the player,
// in which case the pickup no longer exists).
export function getNearestLure(worldPos, maxDist = LURE_EFFECT_RANGE) {
  let best = null;
  let bestD = maxDist * maxDist;
  for (const lure of lures) {
    if (lure.consumed || lure.dead) continue;
    // If the player picked up the lure, the pickup entry is gone — drop it.
    if (!lure.pickupEntry.group.parent) { lure.dead = true; continue; }
    const d = lure.worldPos.distanceToSquared(worldPos);
    if (d < bestD) { best = lure; bestD = d; }
  }
  return best;
}

// Called by the creature AI when it starts eating a lure. Locks the
// pickup (player can't grab it during the meal) and starts the removal
// timer.
export function consumeLure(lure) {
  if (!lure || lure.consumed) return;
  lure.consumed   = true;
  lure.consumedAt = performance.now() / 1000;
  if (lure.pickupEntry) lure.pickupEntry.locked = true;
}
